import React, { useContext, useEffect, useMemo } from 'react';
import { AccountRawData } from '@block-explorer/entities';
import { getAccountWeightFromAccountRawData } from '../utils/account';

export enum DeployFlowType {
  transfer = 'transfer',
  delegation = 'delegation',
  undelegation = 'undelegation',
  purchaseNFT = 'purchaseNFT',
  manageAssociatedKeys = 'manageAssociatedKeys',
}

interface MultisigContextProps {
  deploy?: any;
  activeAccountWeight: number;
  totalSignWeight: number;
  thresholds: any;
  isMultisig: boolean;
  isMultisigKeyManagement: boolean;
  updateTotalSignWeight: (weight: number) => void;
  setAccountWeightAndThresholds: (
    activePublicKey: string,
    accountRawData: AccountRawData | null
  ) => void;
}

const MultisigContext = React.createContext<MultisigContextProps>({
  deploy: null,
  isMultisig: false,
  isMultisigKeyManagement: false,
  activeAccountWeight: 1,
  thresholds: null,
  totalSignWeight: 1,
  updateTotalSignWeight: () => {},
  setAccountWeightAndThresholds: () => {},
});

const { Provider: MultisigContextProvider } = MultisigContext;

export const useMultisigService = () => {
  return useContext(MultisigContext);
};

export const MultisigProvider = (props) => {
  const [multisig, setMultisig] = React.useState<boolean>(false);
  React.useState<boolean>(false);
  const [multisigKeyManagement, setMultisigKeyManagement] =
    React.useState<boolean>(false);
  const [totalSignWeight, setTotalSignWeight] = React.useState<number>(0);
  const [accountWeight, setAccountWeight] = React.useState<number>(1);
  const [thresholds, setThresholds] = React.useState<{
    deployment: number;
    keymanagement: number;
  }>({
    deployment: 0,
    keymanagement: 0,
  });

  useEffect(() => {
    if (props.deploy && props.deploy.deploy) {
      setMultisig(true);
    }
  }, [props.deploy]);

  useEffect(() => {
    const weightIsNotEnough =
      accountWeight + totalSignWeight < thresholds.deployment;
    const weightIsEnough =
      accountWeight + totalSignWeight >= thresholds.deployment;

    const weightIsNotEnoughForKeyManage =
      accountWeight < thresholds.keymanagement;
    const weightIsEnoughForKeyManage =
      accountWeight >= thresholds.keymanagement;

    if (weightIsNotEnough) {
      setMultisig(true);
    } else if (weightIsEnough) {
      setMultisig(false);
    }

    if (weightIsNotEnoughForKeyManage) {
      setMultisigKeyManagement(true);
    } else if (weightIsEnoughForKeyManage) {
      setMultisigKeyManagement(false);
    }
  }, [thresholds, accountWeight, props.deploy]);

  const updateTotalSignWeight = (weight) => {
    setTotalSignWeight(weight);
  };

  const setAccountWeightAndThresholds = (
    activePublicKey: string,
    accountRawData: AccountRawData | null
  ) => {
    if (accountRawData === null) {
      return;
    }

    setAccountWeight(
      getAccountWeightFromAccountRawData(activePublicKey, accountRawData)
    );
    setThresholds({
      deployment: accountRawData?.Account?.action_thresholds.deployment || 1,
      keymanagement:
        accountRawData?.Account?.action_thresholds.key_management || 1,
    });
  };

  const contextProps: MultisigContextProps = useMemo(() => {
    return {
      deploy: props.deploy,
      totalSignWeight,
      thresholds,
      isMultisig: multisig,
      isMultisigKeyManagement: multisigKeyManagement,
      activeAccountWeight: accountWeight,
      updateTotalSignWeight,
      setAccountWeightAndThresholds,
    };
  }, [
    props.deploy,
    multisig,
    multisigKeyManagement,
    totalSignWeight,
    accountWeight,
    thresholds,
    updateTotalSignWeight,
    setAccountWeightAndThresholds,
  ]);

  return <MultisigContextProvider value={contextProps} {...props} />;
};
